import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Rocket, Download, Copy, Loader2, Film, MessageCircle, Facebook, Instagram, Music2, ScrollText, ImageIcon, Globe, ChevronDown, ChevronUp, CheckCircle2,
} from "lucide-react";
import { api, apiError, BACKEND_URL, downloadFile } from "../../lib/api";

const copyText = async (text, msg = "Texto copiado") => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch (err) { console.warn("clipboard:", err); }
};

const Section = ({ label }) => (
  <div className="text-xs uppercase tracking-widest text-emerald-200/60 font-mono mb-2">{label}</div>
);

const VideoCard = ({ m, caption }) => {
  const [showScript, setShowScript] = useState(false);
  const isTikTok = m.material_id === "mn_video_tiktok";

  const publish = async () => {
    if (caption) await copyText(caption, "Texto copiado (incluye tu link personal)");
    if (m.video_url) downloadFile(m.video_url, `multinivel-${m.material_id}.mp4`);
    window.open(isTikTok ? "https://www.tiktok.com/upload" : "https://www.instagram.com/", "_blank");
    toast.success("Video descargado y texto copiado. Sube el video, pega tu texto y publica.");
  };

  return (
    <div data-testid={`mn-material-${m.material_id}`} className="rounded-2xl border border-[#FACC15]/25 p-6" style={{ background: "linear-gradient(160deg, #04231b 0%, #032018 100%)" }}>
      <div className="flex items-center gap-2 text-[#FACC15]"><Film size={18} />
        <h3 className="font-renalife font-bold text-xl text-[#F8FAF8]">{m.title}</h3>
      </div>
      <p className="text-xs text-emerald-100/70 mt-1 mb-4">{m.description}</p>
      {m.video_url ? (
        <div className="rounded-xl overflow-hidden border border-white/10 max-w-sm">
          <video data-testid={`mn-video-${m.material_id}`} src={m.video_url} controls className="w-full max-h-96 bg-black" />
          <button onClick={() => downloadFile(m.video_url, `multinivel-${m.material_id}.mp4`)} className="w-full flex items-center justify-center gap-1.5 text-xs py-2.5 bg-black/40 hover:bg-[#FACC15] hover:text-[#022C22] font-semibold transition-colors">
            <Download size={14} /> Descargar video
          </button>
        </div>
      ) : m.video_status === "processing" ? (
        <div className="rounded-xl border border-dashed border-[#FACC15]/30 p-8 text-center text-sm text-emerald-200/80 max-w-sm">
          <Loader2 className="animate-spin mx-auto mb-2 text-[#FACC15]" size={20} />
          {m.video_progress || "Generando tu video largo…"}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-zinc-500 max-w-sm">
          <Film className="mx-auto mb-2" size={20} /> Video en preparación (lo genera el administrador)…
        </div>
      )}
      {m.video_script && (
        <>
          <button data-testid={`mn-toggle-script-${m.material_id}`} onClick={() => setShowScript((v) => !v)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300/80 hover:text-emerald-200">
            <ScrollText size={13} /> {showScript ? "Ocultar guion" : "Ver guion completo del video"}
          </button>
          {showScript && <pre className="mt-2 whitespace-pre-wrap text-[11px] leading-relaxed text-emerald-100/70 bg-black/30 border border-white/10 rounded-xl p-4 font-mono">{m.video_script}</pre>}
        </>
      )}
      {m.video_url && (
        <button data-testid={`mn-publish-${m.material_id}`} onClick={publish}
          className={`mt-4 flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-all hover:-translate-y-0.5 ${isTikTok ? "bg-white hover:bg-zinc-200 text-black" : "bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 text-white"}`}>
          {isTikTok ? <Music2 size={16} /> : <Instagram size={16} />} Publicar en {isTikTok ? "TikTok" : "Instagram Reels"}
        </button>
      )}
    </div>
  );
};

const ImageCard = ({ m, caption }) => {
  const publish = async (network) => {
    if (caption) await copyText(caption, "Texto copiado (incluye tu link personal)");
    if (m.image_url) downloadFile(`${BACKEND_URL}${m.image_url}`, `multinivel-${m.material_id}.jpg`);
    window.open(network === "facebook" ? "https://www.facebook.com/" : "https://www.instagram.com/", "_blank");
    toast.success("Imagen descargada y texto copiado. Sube la imagen, pega tu texto y publica.");
  };
  return (
    <div data-testid={`mn-material-${m.material_id}`} className="rounded-2xl border border-[#FACC15]/25 p-6" style={{ background: "linear-gradient(160deg, #04231b 0%, #032018 100%)" }}>
      <div className="flex items-center gap-2 text-[#FACC15]"><ImageIcon size={18} />
        <h3 className="font-renalife font-bold text-lg text-[#F8FAF8]">{m.title}</h3>
      </div>
      <p className="text-xs text-emerald-100/70 mt-1 mb-4">{m.description}</p>
      {m.image_url ? (
        <div className="rounded-xl overflow-hidden border border-white/10">
          <img data-testid={`mn-image-${m.material_id}`} src={`${BACKEND_URL}${m.image_url}`} alt={m.title} className="w-full object-cover" />
          <button onClick={() => downloadFile(`${BACKEND_URL}${m.image_url}`, `multinivel-${m.material_id}.jpg`)} className="w-full flex items-center justify-center gap-1.5 text-xs py-2.5 bg-black/40 hover:bg-[#FACC15] hover:text-[#022C22] font-semibold transition-colors">
            <Download size={14} /> Descargar imagen
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-zinc-500">Imagen en preparación…</div>
      )}
      {m.image_url && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button data-testid={`mn-publish-fb-${m.material_id}`} onClick={() => publish("facebook")} className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold bg-[#1877F2] hover:bg-[#3b8bf5] text-white transition-all hover:-translate-y-0.5"><Facebook size={14} /> Facebook</button>
          <button data-testid={`mn-publish-ig-${m.material_id}`} onClick={() => publish("instagram")} className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 text-white transition-all hover:-translate-y-0.5"><ImageIcon size={14} /> Instagram</button>
        </div>
      )}
    </div>
  );
};

export default function RenalifeMultinivel() {
  const [materials, setMaterials] = useState(null);
  const [slug, setSlug] = useState(null);
  const [showSteps, setShowSteps] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/renalife/my-landing").then((r) => setSlug(r.data.slug)).catch((err) => console.warn("my-landing:", err));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/renalife/multinivel");
      setMaterials(data);
      if (data.some((m) => m.video_status === "processing")) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 8000);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  if (!materials) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FACC15]" size={30} /></div>;
  }

  const byId = Object.fromEntries(materials.map((m) => [m.material_id, m]));
  const link = slug ? `${window.location.origin}/m/${slug}` : null;
  const fbText = byId.mn_texto_facebook?.caption
    ? `${byId.mn_texto_facebook.caption}${link ? `\n\n👉 Regístrate para la presentación privada aquí: ${link}` : ""}`
    : "";
  const videos = [byId.mn_video_tiktok, byId.mn_video_instagram].filter(Boolean);
  const images = [byId.mn_imagen_producto, byId.mn_imagen_fila].filter(Boolean);
  const variants = byId.mn_whatsapp?.variants || [];

  const steps = [
    {
      id: "tiktok", title: "Publica el video en TikTok", done: !!byId.mn_video_tiktok?.video_url,
      action: () => {
        if (!byId.mn_video_tiktok?.video_url) return toast.error("El video de TikTok aún no está listo");
        copyText(fbText, "Texto copiado");
        downloadFile(byId.mn_video_tiktok.video_url, "multinivel-tiktok.mp4");
        window.open("https://www.tiktok.com/upload", "_blank");
      },
    },
    {
      id: "instagram-video", title: "Publica el video en Instagram Reels", done: !!byId.mn_video_instagram?.video_url,
      action: () => {
        if (!byId.mn_video_instagram?.video_url) return toast.error("El video de Instagram aún no está listo");
        copyText(fbText, "Texto copiado");
        downloadFile(byId.mn_video_instagram.video_url, "multinivel-instagram.mp4");
        window.open("https://www.instagram.com/", "_blank");
      },
    },
    {
      id: "imagenes", title: "Publica las imágenes con el texto AIDA", done: images.every((i) => i.image_url),
      action: () => {
        copyText(fbText, "Texto copiado");
        images.forEach((i) => i.image_url && downloadFile(`${BACKEND_URL}${i.image_url}`, `multinivel-${i.material_id}.jpg`));
        window.open("https://www.facebook.com/", "_blank");
      },
    },
    {
      id: "whatsapp", title: "Comparte tu landing por WhatsApp", done: !!link,
      action: () => {
        if (!link) return toast.error("Tu link personal aún no está listo");
        const msg = variants[0] ? `${variants[0]}\n\n👉 ${link}` : `Te comparto algo grande que está iniciando 🚀 ${link}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
      },
    },
  ];

  return (
    <div data-testid="renalife-multinivel-page" className="space-y-8">
      <div className="rounded-2xl border border-[#FACC15]/30 p-6 sm:p-8" style={{ background: "linear-gradient(135deg, #022C22, #064E3B)" }}>
        <div className="flex items-center gap-2 text-[#FACC15]"><Rocket size={20} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Campaña lista para publicar</span>
        </div>
        <h1 className="font-renalife font-bold text-3xl sm:text-4xl text-[#F8FAF8] mt-2">
          Campaña <span className="text-[#FACC15]">MULTINIVEL</span>
        </h1>
        <p className="text-sm text-emerald-100/70 mt-2 max-w-2xl">
          Videos largos, imágenes, textos y tu landing personal, diseñados para atraer networkers y líderes de venta directa. Todo listo: tú solo publicas.
        </p>
        <button
          data-testid="publicar-campana-multinivel"
          onClick={() => setShowSteps((v) => !v)}
          className="mt-5 inline-flex items-center gap-2 rounded-xl px-6 py-4 text-base font-black bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 transition-all hover:-translate-y-0.5"
        >
          <Rocket size={18} /> PUBLICAR CAMPAÑA MULTINIVEL {showSteps ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showSteps && (
          <div data-testid="mn-steps-panel" className="mt-5 space-y-2">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 rounded-xl bg-black/30 border border-white/10 px-4 py-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${s.done ? "bg-[#FACC15] text-[#022C22]" : "bg-white/10 text-zinc-400"}`}>{i + 1}</div>
                <div className="flex-1 text-sm font-semibold text-zinc-100">{s.title}</div>
                {s.done && <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />}
                <button data-testid={`mn-step-${s.id}`} onClick={s.action} className="text-xs font-bold rounded-lg px-3 py-2 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 shrink-0">Publicar</button>
              </div>
            ))}
            <p className="text-[11px] text-zinc-500 px-1">Cada paso copia tu texto, descarga el archivo y abre la red social: solo pega, sube y publica.</p>
          </div>
        )}
      </div>

      {link && (
        <div data-testid="mn-landing-link-card" className="rounded-2xl border border-[#FACC15]/25 bg-[#04231b] p-6">
          <div className="flex items-center gap-2 text-[#FACC15] mb-1"><Globe size={16} />
            <h2 className="font-renalife font-bold text-lg text-[#F8FAF8]">Tu landing MULTINIVEL personal</h2>
          </div>
          <p className="text-xs text-emerald-100/70 mb-3">Quien se registre aquí llega a TU WhatsApp y queda en tus Prospectos.</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl bg-black/30 border border-white/10 px-4 py-3">
            <div className="text-[12px] font-mono text-zinc-300 truncate flex-1">{link}</div>
            <div className="flex gap-2 shrink-0">
              <button data-testid="mn-copy-link" onClick={() => copyText(link, "Link copiado")} className="inline-flex items-center gap-1 text-xs font-bold rounded-lg px-3 py-1.5 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300"><Copy size={12} /> Copiar</button>
              <a data-testid="mn-view-link" href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">Ver</a>
              <a data-testid="mn-wa-link" href={`https://wa.me/?text=${encodeURIComponent(`Te comparto algo grande que está iniciando 🚀 ${link}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 bg-green-600 text-white hover:bg-green-500"><MessageCircle size={12} /> WhatsApp</a>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-renalife font-bold text-2xl text-[#F8FAF8] mb-4">Videos largos <span className="text-[#FACC15]">(30-45 seg)</span></h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {videos.map((m) => <VideoCard key={m.material_id} m={m} caption={fbText} />)}
        </div>
      </div>

      <div>
        <h2 className="font-renalife font-bold text-2xl text-[#F8FAF8] mb-4">Imágenes <span className="text-[#FACC15]">para anuncios</span></h2>
        <div className="grid md:grid-cols-2 gap-6">
          {images.map((m) => <ImageCard key={m.material_id} m={m} caption={fbText} />)}
        </div>
      </div>

      <div className="rounded-2xl border border-[#FACC15]/25 bg-[#04231b] p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-renalife font-bold text-lg text-[#F8FAF8]">Texto para Facebook <span className="text-[#FACC15]">(método AIDA)</span></h2>
          {fbText && <button data-testid="mn-copy-fbtext" onClick={() => copyText(fbText)} className="inline-flex items-center gap-1 text-xs text-[#FACC15] hover:text-yellow-300 font-semibold"><Copy size={13} /> Copiar</button>}
        </div>
        {fbText ? (
          <p data-testid="mn-fb-text" className="text-sm text-zinc-200 whitespace-pre-wrap rounded-xl bg-black/30 border border-white/10 p-4">{fbText}</p>
        ) : (
          <div className="rounded-xl border border-dashed border-white/15 p-5 text-center text-sm text-zinc-500">Texto en preparación…</div>
        )}
      </div>

      <div className="rounded-2xl border border-[#FACC15]/25 bg-[#04231b] p-6">
        <h2 className="font-renalife font-bold text-lg text-[#F8FAF8] mb-1">Mensajes de seguimiento <span className="text-[#FACC15]">WhatsApp</span></h2>
        <p className="text-xs text-emerald-100/70 mb-4">Para leads con experiencia en ventas que ya mostraron interés. Elige una variante y envíala.</p>
        {variants.length ? (
          <div className="space-y-3">
            {variants.map((v, i) => (
              <div key={v} data-testid={`mn-wa-variant-${i + 1}`} className="rounded-xl bg-black/30 border border-white/10 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FACC15]/70 mb-1.5">Variante {i + 1}</div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{v}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`mn-wa-copy-${i + 1}`} onClick={() => copyText(v, "Mensaje copiado")} className="inline-flex items-center gap-1 text-xs font-bold rounded-lg px-3 py-1.5 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300"><Copy size={12} /> Copiar</button>
                  <a data-testid={`mn-wa-send-${i + 1}`} href={`https://wa.me/?text=${encodeURIComponent(link ? `${v}\n\n👉 ${link}` : v)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 bg-green-600 text-white hover:bg-green-500"><MessageCircle size={12} /> Enviar por WhatsApp</a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/15 p-5 text-center text-sm text-zinc-500">Mensajes en preparación…</div>
        )}
      </div>
    </div>
  );
}
